import {ReactElement, useEffect, useState} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import {QueryClient, dehydrate, useQueryClient} from '@tanstack/react-query';
import {Box, Container, Flex, Grid, GridItem, Text, useDisclosure} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {setApiLocale} from '@eksab/api';
import {useAppSettingsQuery, useMeQuery, useRegionSettings} from '@eksab/hooks';

import {Header, SponsorImage} from '@eksab/features/competition/components';
import {useCheckSameLineupQuery, useCompetitionQuery} from '@eksab/features/competition/hooks';
import {Competition, CompetitionType, TeamPlayer} from '@eksab/features/competition/types';
import {
  addUIState,
  FilteredPlayerList,
  GridListToggle,
  LineupStats,
  ListLineup,
  SubmitLineupButton,
  usePlayersQuery,
  RoleSelectionActions,
} from '@eksab/features/competition/lineup';
import {useLineupQuery} from '@eksab/features/competition/lineup/useLineupQuery';
import {Formations} from '@eksab/features/competition/lineup/Formations';
import {EditablePlaygroundLineup} from '@eksab/features/competition/lineup/EditablePlaygroundLineup';
import {useTeamsQuery} from '@eksab/features/competition/lineup/useTeamsQuery';
import {EditableSubstitutes} from '@eksab/features/competition/lineup/EditableSubstitutes';
import {
  PlayerStatsModal,
  InvalidPlayerModal,
  ApplySameLineupModal,
} from '@eksab/features/competition/components/modals';
import type {InvalidPlayer} from '@eksab/features/competition/components/modals/invalid-player/types';
import {LineupView} from '@eksab/features/competition/lineup/LineupView';
import {SubstitutesView} from '@eksab/features/competition/lineup/SubstitutesView';
import {useLineupStore} from '@eksab/features/competition/lineup/useLineupStore';
import {GoogleBannerAd} from '@eksab/components';

const px = [4, 5, 8, 32];

type PageProps = {
  competitionId: string;
  initialPlayersQueryKey?: any;
};

export default function Lineup({competitionId}: PageProps) {
  const [invalidPlayers, setInvalidPlayers] = useState<InvalidPlayer[]>([]);
  const applySameLineupDisclosure = useDisclosure();

  const t = useTranslate();
  const {data: me} = useMeQuery();
  const {data: {name = t('app.competition'), state: competitionState, season, budget, sponsors} = {}} =
    useCompetitionQuery(competitionId, {
      staleTime: 5 * 60 * 1000,
    });
  const {data: sameLineup} = useCheckSameLineupQuery(competitionId, {
    onSuccess: ({lineup_flag}) => lineup_flag && applySameLineupDisclosure.onOpen(),
    enabled: !!me,
  });

  const setState = useLineupStore((state) => state.setState);
  const queryClient = useQueryClient();
  const lineupQuery = useLineupQuery(competitionId);

  useEffect(() => {
    useLineupStore.getState().reset();
    return () => useLineupStore.getState().reset();
  }, []);

  useEffect(() => {
    mixpanel?.track('Page View', {page: `Competition`, name, tab: 'Team Setup'});
  }, [name]);

  useEffect(
    function getInvalidPlayers() {
      const players = lineupQuery.data?.players.flat().concat(lineupQuery.data.substitutes) ?? [];

      const invalidPlayers = players.filter(
        (player) => player?.selection_status && player.selection_status.status !== 'valid',
      ) as InvalidPlayer[];

      setInvalidPlayers(invalidPlayers);
    },
    [lineupQuery.data],
  );

  useEffect(() => {
    if (lineupQuery.data) {
      setState({
        players: lineupQuery.data.players,
        formation: lineupQuery.data.formation,
        substitutes: lineupQuery.data.substitutes,
        numberOfPlayers: lineupQuery.data.players.flat().length + lineupQuery.data.substitutes.length,
        overallCost: lineupQuery.data.overallCost,
        indicatorPosition: {position: 'Goalkeeper', index: 0},
      });

      queryClient.setQueriesData(
        {
          predicate: ({queryKey}) => queryKey[0] === 'team-players',
        },
        (players) => (players ? addUIState(players as TeamPlayer[]) : players),
      );
    }

    if (lineupQuery.data === null) useLineupStore.getState().reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineupQuery.data]);

  const isLineupSubmittable = !competitionState || competitionState === 'upcoming';

  return (
    <>
      <Head>
        <meta property="og:title" content={`${name} - ${t('fantasy.lineup')} | ${t('app.name')}`} />
        <title>{`${name} - ${t('fantasy.lineup')} | ${t('app.name')}`}</title>
      </Head>

      {isLineupSubmittable ? (
        <>
          {invalidPlayers.map((invalidPlayer) => (
            <InvalidPlayerModal key={invalidPlayer.id} player={invalidPlayer} />
          ))}

          <LineupStats start={season?.start!} budget={budget} mb={py} />

          <Container w="100%" display="flex" px={px} justifyContent="center">
            <SponsorImage sponsors={sponsors!} />
          </Container>

          <Box mb="30">
            <GoogleBannerAd slotId="gpt-ad-competition-lineup" />
          </Box>

          <Container
            as={Grid}
            display="grid"
            pb={py}
            px={px}
            rowGap="12"
            templateColumns={['100%', null, 'minmax(400px, 1fr) minmax(1rem, 14rem) minmax(21rem, 23.75rem)']}
          >
            <GridItem>
              <Formations />
            </GridItem>

            <GridItem rowSpan={4} display={['none', null, 'block']} />

            <FilteredPlayerList competitionId={competitionId} />

            <GridItem>
              <Flex align="baseline">
                <Text fontSize="larger" fontWeight="800" mb="5" me="auto">
                  {t('fantasy.lineup.step2')}
                </Text>
                <GridListToggle />
              </Flex>

              <LineupView />
            </GridItem>

            <GridItem>
              <Text fontSize="larger" fontWeight="800" mb="5">
                {t('fantasy.lineup.step3')}
              </Text>
              <SubstitutesView />
            </GridItem>

            <Box>
              <Text fontSize="larger" fontWeight="800" mb="5">
                {t('fantasy.lineup.step4')}
              </Text>

              <RoleSelectionActions />
            </Box>

            <GridItem display="flex" justifyContent="center" alignItems="center" px={['0', null]}>
              <SubmitLineupButton w="100%" />
            </GridItem>
          </Container>
        </>
      ) : (
        <LineupDisplay isRunning={competitionState === 'running'} />
      )}

      <PlayerStatsModal hasActions={isLineupSubmittable} />
      <ApplySameLineupModal {...applySameLineupDisclosure} lineup={sameLineup?.team_setup} />
    </>
  );
}

interface LineupDisplayProps {
  isRunning: boolean;
}

const LineupDisplay = ({isRunning}: LineupDisplayProps) => {
  const t = useTranslate();

  // TODO: Use in case of no submitted players
  // const hasPlayers = players.flat().every(Boolean);

  const view = useLineupStore((state) => state.view);

  return (
    <>
      {isRunning && (
        <Text w="full" px="6" py="5" bgColor="gray.600" fontSize="sm" textAlign="center">
          {t('fantasy.lineup.view-only.live')}
        </Text>
      )}

      <Box
        maxW="full"
        w="36.25rem"
        px={[6, null, 0]}
        pt="3.125rem"
        pb="4.375rem"
        mx="auto"
        display="flex"
        flexDir="column"
        gap="10"
        alignItems="center"
      >
        <Box as="section" w="full" display="flex" flexDir="column" gap="5">
          <Box display="flex" justifyContent="space-between" alignItems="center" gap="5">
            <Text textTransform="capitalize" fontSize="xl" fontWeight="800">
              {t('fantasy.lineup.team')}
            </Text>

            <GridListToggle />
          </Box>

          {view === 'pitch' ? (
            <Box>
              <EditablePlaygroundLineup isEditable={false} />
            </Box>
          ) : (
            <ListLineup hasStatsAction />
          )}
        </Box>

        <Box as="section" w="full" display="flex" flexDir="column" gap="5">
          <Text textTransform="capitalize" fontSize="xl" fontWeight="800">
            {t('fantasy.lineup.substitutes')}
          </Text>

          {view === 'pitch' ? (
            <Box w="full" px="5%" py="5" bgColor="gray.600" display="flex" gap="2.5" rounded="md">
              <EditableSubstitutes isEditable={false} />
            </Box>
          ) : (
            <ListLineup substitute />
          )}
        </Box>
      </Box>
    </>
  );
};

Lineup.getLayout = (page: ReactElement) => {
  return (
    <>
      <Header competitionType="fantasy" />
      {page}
    </>
  );
};

export const makeGetStaticProps: (route: 'group-competition' | 'eksab-competition') => GetStaticProps =
  (route) =>
  async ({locale, params}) => {
    const queryClient = new QueryClient();

    setApiLocale(locale);
    try {
      const competition = await queryClient.fetchQuery<Competition>(
        [...useCompetitionQuery.queryKey(params!.slug as string), locale],
        () => useCompetitionQuery.queryFn(params!.slug as string, false),
      );

      const isWrongRoute =
        (route === 'eksab-competition' && competition.creator !== null) ||
        (route === 'group-competition' && competition.creator === null) ||
        competition.type !== CompetitionType.FANTASY;

      if (isWrongRoute) return {notFound: true, revalidate: 1};

      if (competition.state === 'upcoming') {
        const teams = await queryClient.fetchQuery<Team[]>(
          [...useTeamsQuery.queryKey(`${competition.id}`), locale],
          () => useTeamsQuery.queryFn(`${competition.id}`),
        );

        if (teams.length > 0) {
          const initialPlayersQueryKey = {
            q: '',
            competition: `${competition.id}`,
            price_from: 0,
            price_to: 15,
            team: `${teams[0].id}`,
            position: 'Goalkeeper' as const,
          };
          await queryClient.fetchQuery([...usePlayersQuery.queryKey(initialPlayersQueryKey), locale], () =>
            usePlayersQuery.queryFn(initialPlayersQueryKey),
          );
        }
      }

      const [regionSettings] = await Promise.all([
        queryClient.fetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn),
        queryClient.prefetchQuery([...useAppSettingsQuery.queryKey, locale], useAppSettingsQuery.queryFn),
      ]);

      if (route === 'group-competition' && !regionSettings.settings.show_groups) return {notFound: true, revalidate: 1};

      return {
        props: {
          dehydratedState: dehydrate(queryClient),
          competitionId: `${competition.id}`,
        },
      };
    } catch (e) {
      return {notFound: true, revalidate: 1};
    }
  };
export const getStaticProps: GetStaticProps = makeGetStaticProps('eksab-competition');

export function getStaticPaths() {
  return {paths: [], fallback: 'blocking'};
}
